// <purpose>Which statuses are settled (Entschieden: the clerk's yes or no, not yet filed;
// "Genehmigen settles the row", decision 2026-10-23) and the facts read off a settled row. The press
// itself — its optimistic face, card and undo — is services/pressVerdict + hooks/usePress.</purpose>
// <constraints>No React. The statuses are core's own: approved/sent (the yes), declined (the no —
// an ACTIVE status since this decision). Archived rows are filed, never "settled" here.</constraints>

/** The settled statuses: decided, not filed. */
export const SETTLED_STATUSES: ReadonlySet<string> = new Set(['approved', 'sent', 'declined']);

export const isSettledStatus = (status: string | null | undefined): boolean =>
  !!status && SETTLED_STATUSES.has(status);

/** The settled statuses a Neu analysieren may take back — core's ReanalyzableStatuses minus the
 *  open one: the yes and the no. `sent` is a legacy status nothing writes today; it settles a row
 *  but has no undo to withdraw through, so the retry stays off there. */
export const canRetrySettled = (status: string | null | undefined): boolean =>
  status === 'approved' || status === 'declined';

/** The clock a settled row was decided on — the approval's stamp or the verdict's own — for the
 *  Auto button's aging on the Entschieden tab. Null when neither is on the wire. */
export const decidedAtOf = (t: { status: string; approvedAt?: string | null; decline?: { declinedAt: string } | null }): string | null =>
  t.status === 'declined' ? (t.decline?.declinedAt ?? null) : t.status === 'approved' ? (t.approvedAt ?? null) : null;

/** Whether the row carries the clerk's formal yes — standing (approved, the legacy sent) or filed
 *  after it (archived from approved/sent). The „Kopiert" trace's hover reads this: a copy that was
 *  later approved is no longer „nicht förmlich genehmigt" (B5 walk, 2026-10-27). */
export const isFormallyApproved = (t: { status?: string | null; previousStatus?: string | null }): boolean =>
  t.status === 'approved' || t.status === 'sent'
  || (t.status === 'archived' && (t.previousStatus === 'approved' || t.previousStatus === 'sent'));

/** „Kopiert" (the press, decision 2026-10-28): a letter the clerk COPIED and has not approved. Once
 *  approved, the yes badge says it and „Kopiert" would only repeat it. Per letter on a triage row —
 *  a copy claim still standing (approving that letter upgrades its claim) — and per mail on a
 *  letter-flow row, where copying writes the copy stamp and no claim. The receipt is never a copy of
 *  a decision. A copied mail later declined keeps it: both facts are true. One rule for the row, the
 *  detail header and the Uploads row. `claimVia`: claimed kind -> 'approve' | 'copy'. */
export const showsKopiert = (t: {
  status?: string | null; previousStatus?: string | null; copyApprovedAt?: string | null;
  claimVia?: Record<string, string> | null;
}): boolean => {
  const via = Object.entries(t.claimVia ?? {});
  if (via.some(([kind, v]) => v === 'copy' && kind !== 'ack')) return true;
  return !!t.copyApprovedAt && via.length === 0 && !isFormallyApproved(t);
};

/** A list row's claims as a via map (a kind missing from the served map reads as approve). */
export const rowClaimVia = (t: { draftClaims?: readonly string[] | null; draftClaimVia?: Record<string, string> | null }): Record<string, string> =>
  Object.fromEntries((t.draftClaims ?? []).map(kind => [kind, t.draftClaimVia?.[kind] ?? 'approve']));
